import clsx from 'clsx'
import {find} from 'lodash'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import {useIntl} from 'react-intl'
import {useSearchParams} from 'react-router-dom'
import {DatePicker, KTSVG} from '../../../../../../../gori/components'
import {DEFAULT_PAGE, DEFAULT_PAGE_SIZE, FORMAT_DATE} from '../../../../../../../gori/constants'
import {convertUserTimeZone} from '../../../../../../../gori/helpers'
import {useCancelToken, UsePermission} from '../../../../../../../gori/hooks'
import {useA1BatchesProvider, useAuth} from '../../../../../../../gori/providers'
import {
  Button,
  InputSearch,
  SelectFormik,
  ValidationErrorModal,
} from '../../../../../../../gori/widgets'
import {A1_CONSTANTS} from '../../../../core/constants'
import {A1BatchService} from '../../../../core/requests'
import {textByCount} from '../../../../../../../gori/utils/common'
import {AddTicketForm} from './AddTicketForm'
import {mappingStatusTicket} from '../../../../utils'

type Props = {
  show: boolean
  handleCloseModal: () => void
}

const TicketList: React.FC<Props> = ({show, handleCloseModal}) => {
  const intl = useIntl()
  const {currentUser} = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const {newCancelToken, isCancel} = useCancelToken()
  const {isOrigin, isDestination} = UsePermission()
  const {a1Batch, setTicketForum} = useA1BatchesProvider()
  const [tableData, setTableData] = useState<{
    tickets: any
    pagination: any
    hasLoadMore: boolean
  }>({tickets: [], pagination: {}, hasLoadMore: false})
  const [loading, setLoading] = useState<{
    first: boolean
    getTickets: boolean
    refresh: boolean
  }>({
    first: true,
    getTickets: false,
    refresh: false,
  })
  const [totalTabUnread, setTotalTabUnread] = useState({...a1Batch?.ticket_unread_count})

  const [isAddMode, setIsAddMode] = useState(false)
  const [validationErrors, setValidationErrors] = useState<any>()
  const pageCurrent = useRef(Number(DEFAULT_PAGE))

  const tabs = useMemo(() => {
    const allTabs = [
      {
        key: 'customer',
        label: intl.formatMessage({id: 'SHIPBAE_CUSTOMER'}),
        visible: true,
        name: a1Batch?.company_name,
      },
      {
        key: 'origin',
        label: intl.formatMessage({id: 'ORIGIN_PARTNER'}),
        visible: isDestination,
        name: a1Batch?.origin_name,
      },
      {
        key: 'destination',
        label: intl.formatMessage({id: 'DESTINATION_PARTNER'}),
        visible: isOrigin && !!a1Batch?.destination_id,
        name: a1Batch?.destination_name,
      },
    ]

    return allTabs.filter((tab) => tab.visible)
  }, [
    a1Batch?.company_name,
    a1Batch?.origin_name,
    a1Batch?.destination_name,
    intl,
    isDestination,
    isOrigin,
    a1Batch?.destination_id,
  ])

  const tabName = useMemo(() => {
    return (searchParams.get('recipient') || 'customer') as 'customer' | 'origin' | 'destination'
  }, [searchParams])

  const searchText = searchParams.get('s') || null
  const statusFilter = searchParams.get('status') || null
  const dateTicketFrom = searchParams.get('date_ticket_from') || null
  const dateTicketTo = searchParams.get('date_ticket_to') || null

  const handleClose = () => {
    setIsAddMode(false)
    handleCloseModal()
  }

  const ticketFilterParams = useMemo(
    () => ({
      page: DEFAULT_PAGE,
      page_size: DEFAULT_PAGE_SIZE,
      s: searchText,
      status: statusFilter,
      date_ticket_from: dateTicketFrom,
      date_ticket_to: dateTicketTo,
    }),
    [dateTicketFrom, dateTicketTo, searchText, statusFilter]
  )

  const configParams = useMemo(
    () => ({
      ...ticketFilterParams,
      recipient: tabName,
    }),
    [tabName, ticketFilterParams]
  )

  const setTable = useCallback(
    async (refresh = false) => {
      pageCurrent.current = Number(DEFAULT_PAGE)

      try {
        setLoading((prev) => ({...prev, [refresh ? 'refresh' : 'first']: true}))
        const config = {
          params: configParams,
          cancelToken: newCancelToken(),
        }

        const response: any = await A1BatchService.getTickets(a1Batch?.id, config)
        const data = response?.data ?? response

        if (data) {
          setTableData({...data, hasLoadMore: data.pagination.total_pages > 1})
        }
      } catch (error: any) {
        if (isCancel(error)) return
      } finally {
        setLoading((prev) => ({...prev, [refresh ? 'refresh' : 'first']: false}))
      }
    },
    [a1Batch?.id, configParams, isCancel, newCancelToken]
  )

  const fetchTotalTabUnread = useCallback(async () => {
    try {
      const unreadCounts = await Promise.all(
        tabs.map(async (tab) => {
          const config = {
            params: {...ticketFilterParams, recipient: tab.key},
            cancelToken: newCancelToken(),
          }
          const response: any = await A1BatchService.getTickets(a1Batch?.id, config)
          const data = response?.data ?? response

          return {
            key: tab.key,
            count: data?.total_comment_unread ?? 0,
          }
        })
      )

      setTotalTabUnread((prev) =>
        unreadCounts.reduce(
          (total, unread) => ({
            ...total,
            [unread.key]: unread.count,
          }),
          {...prev}
        )
      )
    } catch (error: any) {
      if (isCancel(error)) return
    }
  }, [a1Batch?.id, isCancel, newCancelToken, tabs, ticketFilterParams])

  const fetchMore = async () => {
    if (pageCurrent.current >= tableData?.pagination?.total_pages) {
      setTableData((prev) => ({...prev, hasLoadMore: false}))
      return
    }

    pageCurrent.current++

    try {
      setLoading((prev) => ({...prev, getTickets: true}))
      const config = {
        params: {...configParams, page: pageCurrent.current},
        cancelToken: newCancelToken(),
      }
      const response: any = await A1BatchService.getTickets(a1Batch.id, config)
      if (response) {
        setTableData((prev) => ({...prev, tickets: [...prev.tickets, ...response.tickets]}))
      }
    } catch (error: any) {
      if (isCancel(error)) return
    } finally {
      setLoading((prev) => ({...prev, getTickets: false}))
    }
  }

  useEffect(() => {
    if (show && a1Batch?.id) {
      setTable()
    }
  }, [a1Batch?.id, setTable, show])

  useEffect(() => {
    if (show && a1Batch?.id) {
      fetchTotalTabUnread()
    }
  }, [a1Batch?.id, fetchTotalTabUnread, show])

  const filterStatus = useMemo(() => {
    const statusValue = searchParams.get('status')
    const status: any =
      find(Object.values(A1_CONSTANTS.TICKET_STATUS), {value: statusValue}) || null

    return {
      label: intl.formatMessage({id: status ? status.label : 'ALL'}),
      value: statusValue || '',
    }
  }, [intl, searchParams])

  const handleFilterStatus = useCallback(
    (e) => {
      if (e.value) {
        searchParams.set('status', e.value)
      } else {
        searchParams.delete('status')
      }
      setSearchParams(searchParams)
    },
    [searchParams, setSearchParams]
  )

  const handleViewDetail = (ticket) => {
    searchParams.set('id', ticket.id.toString())
    setSearchParams(searchParams)
    setTicketForum((prev) => ({...prev, detailId: ticket.id.toString()}))
  }

  const handleAddTicket = () => {
    setIsAddMode(true)
  }

  const handleCancelTicket = () => {
    setIsAddMode(false)
  }

  const onClickTab = (key: string) => {
    searchParams.set('recipient', key)
    setSearchParams(searchParams)
  }

  return (
    <>
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}
      <div>
        <div className='d-flex justify-content-between align-items-center px-5 py-6'>
          {isAddMode ? (
            <div className='cursor-pointer' onClick={handleCancelTicket}>
              <KTSVG path='/media/gori/arrows/arrowLeft.svg' className='svg-icon-1 text-gray-700' />
            </div>
          ) : (
            <h4 className='fw-bolder fs-1 m-0'> {intl.formatMessage({id: 'TICKET_FORUM'})}</h4>
          )}
          <div className='d-flex gap-8 align-items-center'>
            {!isAddMode && (
              <Button
                className={clsx('btn btn-sm p-0 text-hover-primary cursor-pointer', {
                  'text-primary': loading.refresh,
                })}
                event={() => {
                  setTable(true)
                }}
              >
                <KTSVG
                  path='/media/gori/a1/sync.svg'
                  className={clsx('svg-icon-1 text-hover-primary', {
                    'spin_reverse text-primary': loading.refresh,
                  })}
                />
                {intl.formatMessage({id: 'REFRESH'})}
              </Button>
            )}

            <Button
              className='btn btn-icon border border-1 border-gray-300 btn-active-light-primary'
              event={handleClose}
            >
              <KTSVG path='/media/gori/common/delete.svg' className='svg-icon-4 text-gray-700' />
            </Button>
          </div>
        </div>

        <div className='ticket-forum-body d-flex flex-column'>
          {isAddMode ? (
            <AddTicketForm
              tabName={tabName}
              recipientOptions={tabs.map((item) => ({
                label:
                  item.key === 'customer'
                    ? intl.formatMessage({id: 'CUSTOMER'})
                    : `${item.label}${item.name ? ` - ${item.name}` : ''}`,
                value: item.key,
              }))}
              onSuccessCallback={(recipient) => {
                if (recipient !== tabName) {
                  onClickTab(recipient)
                } else {
                  setTable()
                }
                setIsAddMode(false)
              }}
              onCancel={handleCancelTicket}
            />
          ) : (
            <>
              <div className='d-flex flex-column flex-md-row justify-content-between flex-wrap gap-4 px-5 py-2 ticket-filter border border-gray-300 border-2'>
                <div className='d-flex align-items-center position-relative my-1'>
                  <InputSearch
                    keySearch='s'
                    className='min-w-250px bg-white border-gray-300'
                    placeholder={`${intl.formatMessage({id: 'SEARCH_TICKET'})}...`}
                  />
                </div>
                <div className='d-flex flex-column flex-sm-row align-items-center gap-4'>
                  <SelectFormik
                    className='w-100 fw-bold fs-5'
                    value={filterStatus}
                    placeholder={intl.formatMessage({id: 'STATUS'})}
                    onChange={handleFilterStatus}
                    options={Object.values(A1_CONSTANTS.TICKET_STATUS)}
                    hasUseIntl={true}
                    emptyDefault={false}
                  />
                  <DatePicker
                    dateFrom='date_ticket_from'
                    dateTo='date_ticket_to'
                    name={intl.formatMessage({id: 'DATE'})}
                    className='w-100 h-45px border-gray-300'
                  />
                </div>
              </div>
              <div
                className='sticky-top mx-0 d-grid'
                style={{gridTemplateColumns: `repeat(${tabs.length}, 1fr)`}}
              >
                {tabs.map((tab) => (
                  <div
                    className={clsx(
                      'd-flex gap-4 justify-content-center align-items-center py-5 border-bottom border-4 cursor-pointer',
                      {
                        'bg-primary-light border-primary': tabName === tab?.key,
                        'bg-transparent border-gray-300': tabName !== tab?.key,
                      }
                    )}
                    key={tab?.key}
                    onClick={() => {
                      onClickTab(tab?.key)
                    }}
                  >
                    <h4
                      className={clsx('fw-bolder', {
                        'text-dark': tabName === tab?.key,
                        'text-gray-600': tabName !== tab?.key,
                      })}
                    >
                      {`${tab?.label} `}
                    </h4>

                    {totalTabUnread[tab?.key] > 0 && (
                      <div
                        className={clsx('px-3 rounded-3 fs-7 h-20px d-flex align-items-center', {
                          'text-white bg-danger border border-danger': tabName === tab?.key,
                          'text-danger bg-danger-light border border-danger': tabName !== tab?.key,
                        })}
                        style={{lineHeight: '20px'}}
                      >
                        {tabName !== tab?.key
                          ? totalTabUnread?.[tab?.key]
                          : `${totalTabUnread[tab?.key]} ${intl
                              .formatMessage({
                                id: 'NEW',
                              })
                              .toLocaleLowerCase()} ${textByCount(
                              totalTabUnread?.[tab?.key],
                              intl.formatMessage({id: 'MESSAGE'}),
                              intl.formatMessage({id: 'MESSAGES'})
                            ).toLocaleLowerCase()}`}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className='flex-fill d-flex flex-column scroll-y scrollbar-width-none'>
                {loading.first || loading.refresh ? (
                  <div className='mt-5'>
                    {Array.from({length: 3}).map((_, idx) => (
                      <div
                        className='d-flex justify-content-between align-items-center py-3 mt-4 border border-gray-300 rounded-4 mx-9 px-4'
                        key={idx}
                      >
                        <div className='col-10 d-flex flex-column'>
                          <div className='fs-1 col-6 placeholder rounded-2 bg-secondary' />
                          <span className='fs-1 col-4 placeholder placeholder-sm rounded-2 bg-secondary mt-2' />
                        </div>
                        <span className='fs-1 col-2 placeholder placeholder-sm rounded-2 bg-secondary' />
                      </div>
                    ))}
                  </div>
                ) : tableData?.tickets.length > 0 ? (
                  <InfiniteScroll
                    dataLength={tableData?.tickets}
                    next={fetchMore}
                    hasMore={tableData?.hasLoadMore}
                    height={'75vh'}
                    loader={
                      loading.getTickets && (
                        <div className='d-flex justify-content-center'>
                          <span className='spinner-border' />
                        </div>
                      )
                    }
                    className='py-9'
                  >
                    {tableData?.tickets.map((ticket: any, idx) => {
                      const status = mappingStatusTicket(ticket?.status)

                      const ticketDate = convertUserTimeZone(
                        ticket.created_at,
                        currentUser,
                        FORMAT_DATE.DATE_TIME
                      )
                      return (
                        <div
                          className={clsx(
                            'position-relative overflow-hidden d-flex justify-content-between align-items-center p-4 mb-5 border border-gray-300 rounded-4 cursor-pointer border-hover-primary bg-hover-primary-light mx-9',
                            {
                              'opacity-75': status === 'closed',
                            }
                          )}
                          key={idx}
                          onClick={() => handleViewDetail(ticket)}
                        >
                          {ticket?.total_comment_unread > 0 && (
                            <div className='w-14px h-100 position-absolute start-0 rounded-start bg-primary' />
                          )}
                          <div
                            className={clsx('me-10 text-truncate align-items-center', {
                              'ms-4': !!ticket?.total_comment_unread,
                              'ms-2': !ticket?.total_comment_unread,
                            })}
                          >
                            <div className='d-flex align-items-center mb-1'>
                              <h4 className='text-truncate mh-0'>
                                {intl.formatMessage({id: 'TICKET'})} #{ticket?.id} - {ticket?.name}
                              </h4>
                              {!!ticket?.total_comment_unread && (
                                <span
                                  className={
                                    'px-3 rounded-3 fs-7 text-white bg-danger ms-2 d-flex justify-content-center align-items-center h-20px'
                                  }
                                >
                                  {ticket.total_comment_unread}
                                </span>
                              )}
                            </div>
                            <span className='text-muted'>
                              {`${intl.formatMessage({id: 'CREATED_ON'})} ${ticketDate}`}
                            </span>
                          </div>
                          <div>
                            <span className={`badge-${status}-ticket rounded-pill px-2`}>
                              {intl.formatMessage({id: status?.toUpperCase()})}
                            </span>
                          </div>
                        </div>
                      )
                    })}
                  </InfiniteScroll>
                ) : (
                  <div className='flex-fill mt-2 d-flex  flex-column justify-content-center align-items-center gap-5'>
                    <div className='d-flex justify-content-between align-items-center p-4 mt-4 border border-gray-300 rounded-2 w-50'>
                      <div className='col-10 d-flex flex-column'>
                        <div className='fs-1 col-6 placeholder rounded-2 bg-secondary' />
                        <span className='fs-1 col-4 placeholder placeholder-sm rounded-2 bg-secondary mt-2' />
                      </div>
                      <span className='fs-1 col-2 placeholder placeholder-sm rounded-2 bg-secondary mt-2' />
                    </div>
                    <h1>{intl.formatMessage({id: 'NO_TICKET_FOUND'})}</h1>
                  </div>
                )}
              </div>
              <div className='px-6 border-top border-gray-300 pt-5'>
                <Button
                  className='btn btn-primary w-100'
                  label={intl.formatMessage({id: 'ADD_NEW_TICKET'})}
                  event={handleAddTicket}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export {TicketList}
